import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  DollarSign,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  useShifts,
  useCurrentShift,
  useOpenShift,
  useCloseShift,
  useShiftSummary,
} from "../api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  Textarea,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  EmptyState,
  DataTable,
  type Column,
} from "@/components/common";
import { formatCurrency, formatDateTime } from "@/lib/utils";
import type { Shift } from "@/types";

export function ShiftsPage() {
  const navigate = useNavigate();
  const [isOpenModalOpen, setIsOpenModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    data: shiftsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useShifts({ page, limit: pageSize });
  const { data: currentShift, refetch: refetchCurrentShift } =
    useCurrentShift();
  const openShift = useOpenShift();
  const closeShift = useCloseShift();

  const shifts = shiftsData?.data ?? [];
  const pagination = shiftsData?.pagination;

  const handleOpenShift = async (openingCash: number, notes?: string) => {
    try {
      await openShift.mutateAsync({
        openingCash: Math.round(openingCash * 100),
        notes,
      });
      toast.success("Shift opened");
      setIsOpenModalOpen(false);
      refetchCurrentShift();
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to open shift");
    }
  };

  const handleCloseShift = async (closingCash: number, notes?: string) => {
    if (!currentShift) return;
    try {
      await closeShift.mutateAsync({
        id: currentShift.id,
        data: { closingCashActual: Math.round(closingCash * 100), notes },
      });
      toast.success("Shift closed");
      setIsCloseModalOpen(false);
      refetchCurrentShift();
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to close shift");
    }
  };

  const getVariance = (shift: Shift) =>
    shift.closingCashActual !== null && shift.closingCashExpected !== null
      ? shift.closingCashActual - shift.closingCashExpected
      : null;

  const columns: Column<Shift>[] = [
    {
      key: "opened",
      header: "Opened",
      cell: (shift) => (
        <div>
          <p>{formatDateTime(shift.openedAt)}</p>
          <p className="text-xs text-muted-foreground">
            {shift.openedBy.firstName} {shift.openedBy.lastName}
          </p>
        </div>
      ),
    },
    {
      key: "closed",
      header: "Closed",
      cell: (shift) =>
        shift.closedAt ? (
          <div>
            <p>{formatDateTime(shift.closedAt)}</p>
            <p className="text-xs text-muted-foreground">
              {shift.closedBy?.firstName} {shift.closedBy?.lastName}
            </p>
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "openingCash",
      header: "Opening Cash",
      cell: (shift) => formatCurrency(shift.openingCash),
    },
    {
      key: "closingCash",
      header: "Closing Cash",
      cell: (shift) =>
        shift.closingCashActual !== null
          ? formatCurrency(shift.closingCashActual)
          : "-",
    },
    {
      key: "variance",
      header: "Variance",
      cell: (shift) => {
        const variance = getVariance(shift);
        if (variance === null) return null;

        return (
          <span
            className={
              variance < 0
                ? "text-destructive"
                : variance > 0
                  ? "text-green-600"
                  : ""
            }
          >
            {variance >= 0 ? "+" : ""}
            {formatCurrency(variance)}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (shift) => (
        <Badge variant={shift.status === "open" ? "success" : "secondary"}>
          {shift.status}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (shift) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/orders?shiftId=${shift.id}`)}
        >
          <FileText className="mr-1.5 h-4 w-4" />
          Orders
        </Button>
      ),
    },
  ];

  const renderMobileCard = (shift: Shift) => {
    const variance = getVariance(shift);

    return (
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="font-medium">{formatDateTime(shift.openedAt)}</p>
            <p className="text-xs text-muted-foreground">
              {shift.openedBy.firstName} {shift.openedBy.lastName}
            </p>
          </div>
          <Badge variant={shift.status === "open" ? "success" : "secondary"}>
            {shift.status}
          </Badge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-muted-foreground">Opening Cash</p>
            <p className="font-medium">{formatCurrency(shift.openingCash)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Closing Cash</p>
            <p className="font-medium">
              {shift.closingCashActual !== null
                ? formatCurrency(shift.closingCashActual)
                : "-"}
            </p>
          </div>
          {variance !== null && (
            <div>
              <p className="text-muted-foreground">Variance</p>
              <p
                className={
                  variance < 0
                    ? "font-medium text-destructive"
                    : variance > 0
                      ? "font-medium text-green-600"
                      : "font-medium"
                }
              >
                {variance >= 0 ? "+" : ""}
                {formatCurrency(variance)}
              </p>
            </div>
          )}
          {shift.closedAt && (
            <div>
              <p className="text-muted-foreground">Closed</p>
              <p className="font-medium">{formatDateTime(shift.closedAt)}</p>
            </div>
          )}
        </div>
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate(`/orders?shiftId=${shift.id}`)}
          >
            <FileText className="mr-1.5 h-4 w-4" />
            View Orders
          </Button>
        </div>
      </div>
    );
  };

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load shifts" onRetry={refetch} />;

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="Shifts"
        description="Manage cash register shifts"
        actions={
          currentShift ? (
            <Button
              variant="destructive"
              onClick={() => setIsCloseModalOpen(true)}
            >
              Close Shift
            </Button>
          ) : (
            <Button onClick={() => setIsOpenModalOpen(true)}>Open Shift</Button>
          )
        }
      />
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Current Shift
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentShift ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">Opened By</p>
                <p className="font-medium">
                  {currentShift.openedBy
                    ? `${currentShift.openedBy.firstName} ${currentShift.openedBy.lastName}`
                    : "—"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(currentShift.openedAt)}
                </p>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">Opening Cash</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(currentShift.openingCash)}
                </p>
              </div>
              <div className="flex items-center justify-center rounded-lg bg-green-50 p-4">
                <Badge variant="success" className="text-lg">
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Shift Open
                </Badge>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <AlertCircle className="h-5 w-5" />
              <p>No shift is currently open</p>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Shift History</CardTitle>
        </CardHeader>
        <CardContent>
          {!shifts.length ? (
            <EmptyState
              icon={Clock}
              title="No shifts yet"
              description="Shifts will appear here once opened"
            />
          ) : (
            <DataTable
              data={shifts}
              columns={columns}
              rowKey={(shift) => shift.id}
              renderMobileCard={renderMobileCard}
              isFetching={isFetching}
              pagination={{
                page,
                pageSize,
                total: pagination?.total ?? 0,
                onPageChange: setPage,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setPage(1);
                },
              }}
            />
          )}
        </CardContent>
      </Card>
      <ShiftDialog
        open={isOpenModalOpen}
        onOpenChange={setIsOpenModalOpen}
        title="Open Shift"
        label="Opening Cash"
        onSubmit={handleOpenShift}
        isLoading={openShift.isPending}
      />
      {currentShift && (
        <CloseShiftDialog
          open={isCloseModalOpen}
          onOpenChange={setIsCloseModalOpen}
          shiftId={currentShift.id}
          onSubmit={handleCloseShift}
          isLoading={closeShift.isPending}
        />
      )}
    </div>
  );
}

interface ShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  onSubmit: (amount: number, notes?: string) => void;
  isLoading: boolean;
}

function ShiftDialog({
  open,
  onOpenChange,
  title,
  label,
  onSubmit,
  isLoading,
}: ShiftDialogProps) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(parseFloat(amount) || 0, notes || undefined);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setAmount("");
      setNotes("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">{label}</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9"
                placeholder="e.g., 100.00"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CloseShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shiftId: string;
  onSubmit: (amount: number, notes?: string) => void;
  isLoading: boolean;
}

function CloseShiftDialog({
  open,
  onOpenChange,
  shiftId,
  onSubmit,
  isLoading,
}: CloseShiftDialogProps) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const { data: summary, isLoading: summaryLoading } = useShiftSummary(shiftId);

  useEffect(() => {
    if (open && summary?.expectedCash !== undefined) {
      setAmount((summary.expectedCash / 100).toFixed(2));
    }
  }, [open, summary?.expectedCash]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(parseFloat(amount) || 0, notes || undefined);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAmount("");
      setNotes("");
    }
    onOpenChange(newOpen);
  };

  const expectedCash = summary?.expectedCash ?? 0;
  const actualCash = (parseFloat(amount) || 0) * 100;
  const variance = actualCash - expectedCash;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close Shift</DialogTitle>
        </DialogHeader>
        {summaryLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2 rounded-lg bg-muted p-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Orders</span>
                <span className="font-medium">
                  {summary?.totalOrders ?? 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gross sales</span>
                <span className="font-medium">
                  {formatCurrency(summary?.grossSales ?? 0)}
                </span>
              </div>
              {(summary?.cardSales ?? 0) > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>· Card</span>
                  <span>{formatCurrency(summary?.cardSales ?? 0)}</span>
                </div>
              )}
              {(summary?.mobileSales ?? 0) > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>· Mobile</span>
                  <span>{formatCurrency(summary?.mobileSales ?? 0)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 text-sm">
                <span className="text-muted-foreground">Opening cash</span>
                <span className="font-medium">
                  {formatCurrency(summary?.shift.openingCash ?? 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Cash sales</span>
                <span className="font-medium text-green-600">
                  +{formatCurrency(summary?.cashSales ?? 0)}
                </span>
              </div>
              {(summary?.totalRefunds ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Refunds</span>
                  <span className="font-medium text-red-600">
                    -{formatCurrency(summary?.totalRefunds ?? 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Expected cash</span>
                <span
                  data-testid="expected-cash"
                  className="font-bold text-primary"
                >
                  {formatCurrency(expectedCash)}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="closing-amount">Actual Closing Cash</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="closing-amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9"
                  placeholder="e.g., 250.00"
                  required
                />
              </div>
            </div>
            {amount && (
              <div
                className={`rounded-lg p-3 text-center ${
                  variance === 0
                    ? "bg-green-50 text-green-700"
                    : variance > 0
                      ? "bg-blue-50 text-blue-700"
                      : "bg-red-50 text-red-700"
                }`}
              >
                <span className="text-sm">
                  {variance === 0
                    ? "✓ Cash matches expected"
                    : variance > 0
                      ? `+${formatCurrency(variance)} over`
                      : `${formatCurrency(variance)} short`}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="close-notes">Notes (Optional)</Label>
              <Textarea
                id="close-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any discrepancies or notes..."
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} variant="destructive">
                {isLoading ? "Closing..." : "Close Shift"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
